<template>
    <div style="position: relative; padding-bottom: 10px">
        <v-text-field
            v-model="input"
            v-bind="$attrs"
            class="base-input v-text-no-shadow v-input-restrict-height mobile"
            :class="[{ 'text-uppercase': uppercase }]"
            :color="color"
            :name="name"
            :id="name"
            :aria-label="ariaLabel === null || ariaLabel === undefined ? makeReadable(name) : ariaLabel"
            :label="label"
            :solo="type === 'solo'"
            :outlined="type === 'outlined'"
            :rules="rules"
            :placeholder="placeholder"
            validate-on-blur
            :hide-details="true"
            ref="inputTextBox"
            @input="$emit('input', input)"
            @keypress="keypress"
            @click:append="$emit('submit', input)"
            @keyup.enter="$emit('submit', input)"
            @keyup="applyPhoneMask($event, input)"
            style="z-index: 1;"
            :aria-describedby="name + '-error'"
            :required="this.required"
            :autocomplete="this.autocomplete"
        />
        <span
          v-if="arrow"
          class="submit-arrow v-icon notranslate v-icon--link mdi mdi-arrow-right theme--light"
          aria-hidden="true"
          @click="$emit('submit', input)"/>
        <div
            :id="name + '-error'"
            style="z-index: 0"
            class="error-msg with-background"
            :class="{ open: showErrorMessage }"
        >
            {{ errorMessage | uppercase }}
        </div>
    </div>
</template>

<script>
import { maskPhone } from '../../utils/phone-mask'

export default {
  name: 'BaseInput',
  props: {
    label: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'outlined'
    },
    border: {
      type: Boolean,
      default: true
    },
    rules: {
      type: Array,
      default: () => []
    },
    maxLength: {
      type: Number,
      default: null
    },
    restrictLength: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: null
    },
    numeric: {
      type: Boolean,
      default: false
    },
    alpha: {
      type: Boolean,
      default: false
    },
    nameField: {
      type: Boolean,
      default: false
    },
    email: {
      type: Boolean,
      default: false
    },
    phone: {
      type: Boolean,
      default: false
    },
    uppercase: {
      type: Boolean,
      default: true
    },
    noAsterisk: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'accent'
    },
    placeholder: {
      type: String,
      default: ''
    },
    arrow: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      input: this.$attrs.value,
      showErrorMessage: false,
      errorMessage: false
    }
  },
  watch: {
    $attrs () {
      if (this.input !== this.$attrs.value) {
        this.input = this.$attrs.value
      }
    }
  },
  mounted () {
    const input = this.$refs.inputTextBox
    const original = input.validate
    input.validate = (force, value) => {
      const isValid = original(force, value)
      this.validate()
      return isValid
    }
  },
  created () {
    if (this.required) {
      this.rules.push((value) => !!value || 'Required')
    }
    if (this.maxLength) {
      this.rules.push(
        (value) =>
          (value && value.length <= this.maxLength) ||
                    `Length must be less than ${this.maxLength}`
      )
    }
    if (this.email) {
      this.rules.push(
        (value) =>
          !value ||
                    /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(value) ||
                    'Must be a valid email'
      )
    }
    if (this.phone) {
      this.rules.push(
        value =>
          (!value ||
          /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/.test(value)) ||
                    'Must be a valid U.S. Phone number'
      )
    }
    if (this.nameField) {
      this.rules.push(
        (value) =>
          !value ||
          /^[a-zA-Z\s\-'.]+$/.test(value) ||
          'Only letters, spaces, hyphens, apostrophes, and periods are allowed'
      )
    }
  },
  methods: {
    keypress ($event) {
      if (this.numeric && !$event.key.toString().match(/^[0-9]/)) {
        $event.preventDefault()
      } else if (
        this.alpha &&
                !$event.key.toString().match(/[a-zA-Z\s]+/)
      ) {
        $event.preventDefault()
      } else if (
        this.nameField &&
                !$event.key.toString().match(/[a-zA-Z\s\-'.]/)
      ) {
        $event.preventDefault()
      } else if (
        this.restrictLength &&
                $event.target.value.length === this.maxLength
      ) {
        $event.preventDefault()
      }
    },
    focus () {
      var el = this.$refs.inputTextBox.$el.getElementsByTagName(
        'input'
      )[0]
      if (el) {
        el.setAttribute('tabIndex', '-1')
        el.focus()
        el.removeAttribute('tabIndex')
      }
    },
    validate () {
      this.$nextTick(() => {
        this.showErrorMessage = !this.isValid()
        this.errorMessage = this.getErrorMessage()
      })
    },
    isValid () {
      const input = this.$refs.inputTextBox
      if (!input) return true
      if (input.isFocused || !input.hasFocused) return true
      return input.valid
    },
    getErrorMessage () {
      const input = this.$refs.inputTextBox
      if (input && input.errorBucket && input.errorBucket.length) {
        return input.errorBucket[0]
      }
      return ''
    },
    makeReadable (str) {
      // Incoming strings are expected to be in Camelcase i.e. likeThis.
      // We find the next capitilized letter and put a space in front of it.
      return str.replace(/([A-Z])/g, ' $1').trim()
    },
    applyPhoneMask (event, string) {
      if (this.phone) {
        const target = event.target
        let position = target.selectionStart

        const { str, e, pos } = maskPhone(event, string)
        this.input = str
        // Ensures formatting applies while user is typing.
        this.$emit('input', str)
        this.event = e
        position = pos

        this.$nextTick(() => {
          target.setSelectionRange(position, position)
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/styles/variables';

.base-input {
    min-height: 50px !important;

    &.mobile {
        min-height: 35px !important;
        font-size: $font-input-size !important;
    }

    label {
        text-transform: uppercase !important;
        font-size: $font-small;
    }

    :focus{
        border-color: #0067b8;
        box-shadow: 0 0 4px 2px #0067b8;
    }
}

.error-msg {
    position: absolute;
    bottom: 20px;
    line-height: 12px;
    font-size: 12px;
    word-break: break-word;
    word-wrap: break-word;
    color: #BB492E;
    transition-property: all;
    transition-duration: 0.3s;
    transition-timing-function: ease-in-out;
    opacity: 0;

    &.open {
        bottom: 0px;
        opacity: 1;
    }
}

.with-background {
    hyphens: none;
    border-radius: 7px;
    box-shadow: 0px 0px 3px 3px #fff;
    background: #fff;
    padding: 0 3px 0 0;
    display: inline-block;
}

.submit-arrow {
  position: absolute !important;
  bottom: 22px;
  right: 12px;
  z-index: 100;
}
</style>
